import { Injectable } from '@angular/core';
import { Restangular } from 'ngx-restangular';

export interface PlaidSandboxTemplateTransaction {
  key: string;
  merchant: string;
  teamCategoryName: string;
  seedCategoryName?: string;
  categoryId: string;
  categories: string[];
  amount: number;
  daysAgo: number;
  expectedBehavior: string;
}

export interface PlaidSandboxCustomTransactionInput {
  merchant: string;
  teamCategoryName: string;
  seedCategoryName?: string;
  amount: number;
  daysAgo: number;
  expectedBehavior?: string;
}

export interface PlaidSandboxTemplate {
  id: string;
  label: string;
  purpose: string;
  tags: string[];
  transactions: PlaidSandboxTemplateTransaction[];
}

export interface PlaidSandboxAccount {
  userId: string;
  accessToken: string;
  accountId: string;
  name: string;
  mask: string | null;
  type: string | null;
  subtype: string | null;
  institutionName: string | null;
  extraStatus: string | null;
}

export interface PlaidSandboxVerificationIssue {
  transactionId: string;
  name: string;
  expectedBehavior: string | null;
  expectedTeamCategoryName: string;
  actualTeamCategoryName: string | null;
  actualHomeVisibility: boolean;
}

export interface PlaidSandboxVerificationSummary {
  totalTransactions: number;
  expectedHomeVisibleCount: number;
  actualHomeVisibleCount: number;
  passedCount: number;
  failedCount: number;
  issues: PlaidSandboxVerificationIssue[];
}

export interface PlaidSandboxScenarioTransaction {
  transactionId: string;
  name: string;
  amount: number;
  transactionDate: string;
  categoryId: string;
  categories: string[];
  pending: boolean;
  teamEligible: boolean;
  teamPaid: boolean;
  teamAmountReimbursed: number | null;
  visibleInHomeReimbursements: boolean;
  actualTeamCategoryName: string | null;
  verification: {
    expectedBehavior: string | null;
    expectedTeamCategoryName: string;
    expectedHomeVisibility: boolean | null;
    actualHomeVisibility: boolean;
    passed: boolean;
    hasExpectation: boolean;
  };
}

export interface PlaidSandboxScenarioResult {
  scenarioRunId: string;
  userId: string;
  targetAccountId: string;
  teamId: string;
  templateIds: string[];
  createdTransactionIds: string[];
  verification: PlaidSandboxVerificationSummary;
  transactions: PlaidSandboxScenarioTransaction[];
}

export interface PlaidSandboxCurrentScenarioRun {
  scenarioRunId: string;
  teamId: string | null;
  templateIds: string[];
  transactionCount: number;
  latestTransactionDate: string;
  createdTransactionIds: string[];
  verification: PlaidSandboxVerificationSummary;
  transactions: PlaidSandboxScenarioTransaction[];
}

export interface PlaidSandboxCurrentAccountData {
  userId: string;
  targetAccountId: string;
  hasToolGeneratedData: boolean;
  totalScenarioRuns: number;
  totalTransactions: number;
  latestScenarioRunId: string | null;
  statusCounts: {
    pending: number;
    teamEligible: number;
    teamPaid: number;
    visibleInHomeReimbursements: number;
  };
  verification: PlaidSandboxVerificationSummary;
  scenarioRuns: PlaidSandboxCurrentScenarioRun[];
}

export interface PlaidSandboxWipeResult {
  userId: string;
  targetAccountId: string;
  wipeMode: 'selected_run' | 'last_run' | 'all_tool_generated_for_account';
  scenarioRunId: string | null;
  deletedCount: number;
  deletedTransactionIds: string[];
}

@Injectable({
  providedIn: 'root'
})
export class PlaidSandboxService {
  constructor(private restangular: Restangular) {}

  private baseRequest() {
    return this.restangular.one('v4').one('admin').one('sandbox').one('plaid');
  }

  listTemplates(): Promise<PlaidSandboxTemplate[]> {
    return this.baseRequest().all('templates').getList().toPromise() as Promise<PlaidSandboxTemplate[]>;
  }

  listUserAccounts(userId: string): Promise<PlaidSandboxAccount[]> {
    return this.baseRequest().one('users', userId).all('accounts').getList().toPromise() as Promise<PlaidSandboxAccount[]>;
  }

  getCurrentAccountData(userId: string, accountId: string): Promise<PlaidSandboxCurrentAccountData> {
    return this.baseRequest().one('users', userId).one('accounts', accountId).one('current').get().toPromise();
  }

  applyScenario(payload: {
    userId: string;
    teamId: string;
    targetAccountId: string;
    templateIds: string[];
    customTransactions?: PlaidSandboxCustomTransactionInput[];
    wipeBeforeApply: boolean;
    verificationMode?: string;
  }): Promise<PlaidSandboxScenarioResult> {
    return this.baseRequest().all('scenarios').customPOST(payload, 'apply').toPromise();
  }

  wipeScenario(payload: {
    userId: string;
    targetAccountId: string;
    mode: 'selected_run' | 'last_run' | 'all_tool_generated_for_account';
    scenarioRunId?: string;
  }): Promise<PlaidSandboxWipeResult> {
    return this.baseRequest().all('scenarios').customPOST(payload, 'wipe').toPromise();
  }

  getScenarioRun(scenarioRunId: string): Promise<PlaidSandboxScenarioResult> {
    return this.baseRequest().one('scenarios', scenarioRunId).get().toPromise();
  }
}
